import { EventAggregator, type Subscription } from "aurelia-event-aggregator";
import { bindable } from "aurelia-templating";
import { autoinject, transient } from "aurelia-dependency-injection";
import { computedFrom } from "aurelia-binding";
import { Reflection } from "../../helpers/reflection";
import { Messages } from "../../constants/event-messages.constants";
import { DateTimeHelper } from "../../helpers/date-time.helper";
import { StringHelper } from "../../helpers/string.helper";
import { MobileHelper } from "../../helpers/mobile-helper";
import { DateExpressionParser } from "../../helpers/date-expressions.helper";
import { ConfigService } from "../../services/basic/config.service";
import * as moment from "moment";
import "./bootstrap-datetimepicker.js";

//Load styles
require("./comp-date-picker.scss");

@autoinject
@transient()
export class CompDatePicker {

    private _options: any = {
        widgetParent: $("picker-container div"),
        allowInputToggle: false
    };
    private _picker: HTMLInputElement;
    private _datePickerController: any;
    private _formateKey: string = "format";
    private _useCurrentKey: string = "useCurrent";
    private _showTodayButtonKey: string = "showTodayButton";
    private _changeEventKey: string = "dp.change";
    private _datePickerId: string = "DateTimePicker";
    private _fKey: string = "_f";
    private _dKey: string = "_d";
    private _subscriptionToDocumentClickEvent: Subscription;
    private _intervalHandler: any;
    private _isVisible: boolean;
    private nullDate : boolean = false;

    public readOnlyValue: any;
    public isMobile: boolean = MobileHelper.isMobileDevice();
    public isAttached: boolean;

    @computedFrom(Reflection.getPropertyPath<CompDatePicker>(z => z._isVisible))
    public get isVisible() {
        return this._isVisible;
    }

    public set picker(value: HTMLInputElement) {
        this._picker = value;
        if (value) {
            this.dateChanged(this.date, null);
        }
    }

    public get picker(): HTMLInputElement {
        return this._picker;
    }

	@computedFrom(Reflection.getPropertyPath<CompDatePicker>(z => z.dateFormat))
    public get hasTimeOnly() {
        return this.hasTime() && !this.hasDate();
    }

    @bindable
    public keepInvalid: boolean = false;

    @bindable
    public useStrict: boolean = true;

    @bindable
    public tab: number;

    @bindable
    public date: Date | string;

    @bindable
    public minDate: Date | string;

    @bindable
    public maxDate: Date | string;

    @bindable
    public showPickerButton: boolean = true;

    @bindable
    public placeholder: string = "";

    @bindable
    public readonly: boolean;

    @bindable
    public setDefault: boolean = true;

    @bindable
    public disabled: boolean;

    @bindable
    public label: string;

    @bindable
    public info: string = "";

    @bindable
    public infoBottom: string = "";

    @bindable
    public showClose: boolean = false;

    @bindable
    public disabledDates: (Date | moment.Moment | string)[] = [];

    @bindable
    public showClear: boolean = false;

    @bindable
    public dateFormat: string;

    @bindable
    public defaultTime: string = "00:00:00";

    @bindable
    public showTodayButton: boolean = false;

    @bindable
    public daysOfWeekDisabled: number[] = [];

    @bindable
    public hasTimezone: boolean = true;

    constructor(private eventAggregator: EventAggregator, private element: Element, private configService: ConfigService) {
        this.dateFormat = this.configService.config.common.defaultDateTimeFormat;
    }

    public hide = () => {
        if (this && this._datePickerController && this._isVisible) {
            const picker = $(this.picker);
            if (picker) {
                const controller = picker.data(this._datePickerId);
                if (controller && !this.isTextSelected()) {
                    controller.hide();
                }
            }
        }
    }

    isTextSelected = () => { // only allow input[type=text]/textarea
        if (this.picker.tagName === "TEXTAREA" ||
            (this.picker.tagName === "INPUT" && this.picker.type === "text")) {
            return this.picker.value.substring(this.picker.selectionStart,
                this.picker.selectionEnd) != null;
            // or return the return value of Tim Down's selection code here
        }
        return false;
    }
    public attached(): void {
        this._subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, this.hide);
        this.isAttached = true;

        //'nullDate' is true when the picker was cleared when not attached.
        if(this.nullDate && this.picker)
        {
            this.date = null;
            this.nullDate = false;
        }

        this.readOnlyValue = (this.date && !DateExpressionParser.isValidExpression(this.date ? this.date.toString() : null)) ? moment(this.date).format(this.dateFormat) : this.date;
        if (this._options[this._formateKey] === undefined) {
            this._options[this._formateKey] = this.dateFormat ? this.dateFormat : this.configService.config.common.defaultDateFormat;
        }
        this._options[this._showTodayButtonKey] = this.showTodayButton;
        this._options.keepInvalid = this.keepInvalid;
        this._options.useStrict = this.useStrict;
        this._options.showClose = this.showClose;
        this._options.showClear = this.showClear;
        this._options.daysOfWeekDisabled = this.daysOfWeekDisabled;
        this._options.ignoreReadonly = true;
        //this._options["debug"] = true;
        this._options[this._useCurrentKey] = false;
        const picker: any = $(this.picker);
        if (picker.datetimepicker) {
            this._datePickerController = picker.datetimepicker(this._options);

            picker.data(this._datePickerId).disabledDates(this.disabledDates);
            this.minDateChanged();
            this.maxDateChanged();
            this.disabledDatesChanged();

            this._datePickerController.on("dp.show", (): void => {
                setTimeout(() => {
                    this._isVisible = true;
                    this.eventAggregator.publish(Messages.showDatePicker);
                });

            });
            this._datePickerController.on("dp.hide", (): void => {
                const pickerValue = this.picker.value;

                this.eventAggregator.publish(Messages.hideDatePicker);
                this._isVisible = false;
                if (pickerValue && DateExpressionParser.isValidExpression(pickerValue)) {
                    this.date = pickerValue;
                }
                else if (pickerValue && moment(pickerValue, this.dateFormat, true).isValid()) {
                    if (moment(pickerValue).format(this.dateFormat) != moment(this.date).format(this.dateFormat)) {
                        const val = moment(pickerValue, this.dateFormat, true).toDate();
                        if (!this.setDate(val)) {
                            this.date = val;
                        }
                    }
                }
                else {
                    this.picker.value = '';
                    this.date = null;
                }
            });
            this._datePickerController.on(this._changeEventKey, (ev: any): void => {
                let newDate: any;
                if (ev.date === false) {
                    this.setDate(null);
                    return;
                } else if (ev.date[this._fKey] && StringHelper.endsWith(ev.date[this._fKey], "Z") || ev.date._isUTC) {
                    newDate = moment(ev.date);
                } else {
                    newDate = moment(moment(ev.date[this._dKey]).format(this.dateFormat), this.dateFormat);
                }
                if (this.date != null && this.date !== undefined &&
                    this.date !== "" && newDate &&
                    ((typeof this.date === "object" && (this.date as Date).toJSON() !== newDate.toJSON()) ||
                        (typeof this.date === "string" && this.date !== newDate.toJSON()))) {
                    this.setDate(typeof this.date === "string" ? newDate.toJSON() : newDate.toDate());
                } else if (!this.date && this.setDefault && newDate && newDate.format('DD/MM/YYYY HH:mm') != moment().utc().format('DD/MM/YYYY HH:mm')) {
                    newDate = this.setDefaultTime(newDate);
                    this.setDate(typeof this.date === "string" ? newDate.toJSON() : newDate.toDate());
                } else if (newDate) {
                    this.setDate(typeof this.date === "string" ? newDate.toJSON() : newDate.toDate());
                }
            });

            const isDateExpression = DateExpressionParser.isValidExpression(this.date ? this.date.toString() : null);
            if (this.date && !isDateExpression) {
                this.setUiDate(this.date, null);
            } else if (this.date && isDateExpression) {
                this.picker.value = this.date.toString();
            }
        }

        this._intervalHandler = setInterval(() => {
            if (this._isVisible && this.picker) {
                const top = $(this.picker).offset().top;
                const left = $(this.picker).offset().left;
                $("picker-container").css({ left: left + "px", top: top + "px" });
            }
        }, 100);
    }


    public detach(): void {
        clearInterval(this._intervalHandler)
    }

    public setDefaultTime(date: any): any {
        const arr: string[] = this.defaultTime.split(":");
        date = date.hour(Number(arr[0]));
        date = date.minute(Number(arr[1]));
        date = date.second(Number(arr[2]));
        return date;
    }

    public openDatePicker(event: Event): void {
        if (this._datePickerController && !this.readonly && !this.disabled) {
            if (!this._options.widgetParent || this._options.widgetParent.length == 0) {
                this._options.widgetParent = $("picker-container div");
                this._datePickerController.data(this._datePickerId).widgetParent(this._options.widgetParent);
            }
            $(this.picker).data(this._datePickerId).show();
            if (event) {
                event.stopPropagation();
            }
        }
    }

    //This is needed for IOS to avoid the cursor when input is readonly
    public onFocus($event) {
        $event.stopPropagation();
        $event.preventDefault();
        if ($(this.picker).is('[readonly]')) {
            this.picker.blur();
        }
    }

    public minDateChanged(): void {
        if (this._datePickerController) {
            const picker = $(this.picker).data(this._datePickerId);
            if (!picker) {
                return;
            }
            picker.minDate(this.minDate ? moment(this.minDate) : false);
        }
    }

    public disabledDatesChanged() {
        if (this._datePickerController && this.disabledDates && this.disabledDates.length > 0) {
            $(this.picker).data(this._datePickerId).disabledDates(this.disabledDates.map(x => moment(this.getDate(x)).format(this.dateFormat)));
            const disabledDates = $(this.picker).data(this._datePickerId).disabledDates();
            let momentDate = moment(this.date);
            if (disabledDates[moment(this.date).format('YYYY-MM-DD')] === true) {
                while (disabledDates[momentDate.format('YYYY-MM-DD')] === true) {
                    momentDate = momentDate.add('days', 1);
                }
                this.setDate(momentDate.toDate());
            }
        }
    }

    public daysOfWeekDisabledChanged() {
        if (this._datePickerController) {
            const pickerId = $(this.picker).data(this._datePickerId);
            if (pickerId) {
                pickerId.daysOfWeekDisabled(this.daysOfWeekDisabled);
            }
        }
    }

    public maxDateChanged(): void {
        if (this._datePickerController) {
            const pickerId = $(this.picker).data(this._datePickerId);
            if (pickerId) {
                pickerId.maxDate(this.maxDate ? moment(this.maxDate) : false);
            }
        }
    }

    public dateChanged(newValue: Date | string, oldValue: Date | string): void {
        if (newValue == oldValue) {
            return;
        }
        if (newValue == oldValue) {
            return;
        }
        if (!this.isAttached || !this.picker) {
            //if new value is null (meaning the date was cleared)
            //and the date-picker is not attached yet, the date is set back to the old value and the
            // 'nullDate' is set to true which when attached sets the date to null
            if(newValue === null) {
                this.nullDate = true;
                this.date = oldValue;
            }
            return;
        }
        const isDateExpression = DateExpressionParser.isValidExpression(this.date ? this.date.toString() : null);
        if (this.date && isDateExpression) {
            return;
        }

        if (newValue && oldValue) {
            if (this.date && isDateExpression) {
                return;
            }
            const moment1 = moment(newValue);
            const moment2 = moment(oldValue);
            const date1 = moment1.format(this.dateFormat);
            const date2 = moment2.format(this.dateFormat);
            if (moment.isMoment(oldValue) == moment.isMoment(newValue) &&
                date1 == date2 &&
                this.picker.value == date1 &&
                (!this.hasTime() || !this.hasDate())) {
                this.readOnlyValue = date1 ? date1 : null;
                return;
            }
        }
        if (this._datePickerController) {
            this.setUiDate(newValue, oldValue);
        }
        setTimeout(() => {
            const paramsEvent: any = { detail: {}, bubbles: true };
            const changeDateEvent: CustomEvent = new CustomEvent("date-changed", paramsEvent);
            this.element.dispatchEvent(changeDateEvent);
        });
    }

    private setUiDate(newValue: string | Date, oldValue: string | Date) {
        $(this.picker).data(this._datePickerId).date(newValue ? this.getDate(newValue) : null);
        this.readOnlyValue = newValue ? moment(this.getDate(newValue)).format(this.dateFormat) : null;
        if (newValue !== null && !oldValue && this.minDate) {
            $(this.picker).data(this._datePickerId).minDate(moment(this.minDate));
        }
    }

    public setDate(val): boolean {
        try {
            if (!this.date || !val || DateTimeHelper.castDate(this.date).getTime() != DateTimeHelper.castDate(val).getTime()
                || DateTimeHelper.castDate(this.date).getDate() != DateTimeHelper.castDate(this.date).getDate()) {
                this.date = this.getDate(val);
            }
            const dateChangedEvent: CustomEvent = new CustomEvent("date-changed", { detail: { date: this.date }, bubbles: true });
            this.element.dispatchEvent(dateChangedEvent);
            return true;

        } catch (e) {
            return false;
        }
    }

    private getDate(val): Date {
        if (!val) {
            return val;
        }

        //to make sure dates used are always in same format
        if (this.hasTimezone) {
            val = moment(val).format(this.dateFormat);
        }

        // make date equivalent in utc
        if (val && !this.hasTime()) {
            const dateString = DateTimeHelper.getDateString(DateTimeHelper.castDate(val));
            if (this.hasTimezone) {
                if (dateString != moment(val, this.dateFormat).toJSON().split("T")[0]) {
                    if (val instanceof Date) {
                        return val;
                    } else {
                        const momentDate = moment(val, this.dateFormat);
                        return momentDate.toDate();
                    }
                }
            } else {
                const json = moment(val).toJSON();
                if (json && dateString != json.split("T")[0]) {
                    if (val instanceof Date) {
                        val.setMinutes(val.getTimezoneOffset() * -1);
                        return val;
                    } else {
                        const momentDate = moment(val);
                        val = momentDate.add("minute", Math.abs(momentDate.utcOffset()));
                        return val.toDate();
                    }
                }
            }
        }
        return val ? (this.hasTimezone ? moment(val, this.dateFormat).toDate() : moment(val).toDate()) : null;
    }

    public dateFormatChanged(newValue: Date): void {
        if (!this.isAttached) { return; }
        this._options[this._formateKey] = newValue;
        if (this._datePickerController && newValue && this.picker) {
            $(this.picker).data(this._datePickerId).format(newValue);
            this.disabledDatesChanged();
        }
    }

    public detached(): void {
        if (this._subscriptionToDocumentClickEvent) {
            this._subscriptionToDocumentClickEvent.dispose();
        }
    }

    private isEnabled = (granularity) => {
        if (typeof granularity !== 'string' || granularity.length > 1) {
            throw new TypeError('isEnabled expects a single character string parameter');
        }
        switch (granularity) {
            case 'y':
                return this.dateFormat.indexOf('Y') !== -1;
            case 'M':
                return this.dateFormat.indexOf('M') !== -1;
            case 'd':
                return this.dateFormat.toLowerCase().indexOf('d') !== -1;
            case 'h':
            case 'H':
                return this.dateFormat.toLowerCase().indexOf('h') !== -1;
            case 'm':
                return this.dateFormat.indexOf('m') !== -1;
            case 's':
                return this.dateFormat.indexOf('s') !== -1;
            default:
                return false;
        }
    }

    private hasTime = () => {
        return (this.isEnabled('h') || this.isEnabled('m') || this.isEnabled('s'));
    }

    private hasDate = () => {
        return (this.isEnabled('y') || this.isEnabled('M') || this.isEnabled('d'));
    }
}
